<script setup>
import { ref, inject } from "vue";
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";
import { SearchIcon, ChevronDoubleRightIcon } from "@heroicons/vue/outline";
import { useRouter } from "vue-router";
import { debounce as _debounce } from "lodash-es";
import Bugsnag from "@bugsnag/js";
import { useFileManagerStore } from "@/stores/filemanager";

const axios = inject("axios");
const router = useRouter();
const fileManagerStore = useFileManagerStore();

const isOpen = ref(false);
const search = ref(null);
const isSearching = ref(false);
const selectedCategory = ref(null);

// Use results object instead of individual refs
const results = ref({
  units: [],
  sub_modules: [],
  modules: [],
  users: [],
  playlists: [],
  groups: [],
  files: [],
  categories: [],
  news_feeds: [],
  scorm_courses: [],
});

// Individual loading states for each filter type
const loadingStates = ref({
  videos: false,
  "pryor courses": false,
  courses: false,
  categories: false,
  modules: false,
  users: false,
  playlists: false,
  "resource manager": false,
  groups: false,
  "news feed": false,
});

// Map filter names to result keys
const filterToResultKey = {
  videos: "units",
  "pryor courses": "scorm_courses",
  courses: "modules",
  categories: "categories",
  modules: "sub_modules",
  users: "users",
  playlists: "playlists",
  "resource manager": "files",
  groups: "groups",
  "news feed": "news_feeds",
};

const items = [
  "all",
  "pryor courses",
  "courses",
  "categories",
  "modules",
  "videos",
  "users",
  "playlists",
  "resource manager",
  "groups",
  "news feed",
];

const closeModal = () => {
  isOpen.value = false;
};
const openModal = () => {
  isOpen.value = true;
};

// Make API call for a specific filter type
const searchByFilter = async (filterType, searchQuery = "") => {
  if (!searchQuery.length && !selectedCategory.value) {
    return;
  }

  const resultKey = filterToResultKey[filterType];
  if (!resultKey) return;

  // Clear previous results for this filter type
  results.value[resultKey] = [];
  loadingStates.value[filterType] = true;

  try {
    const { data } = await axios.get("/api/search", {
      params: {
        search_query: searchQuery,
        filter: filterType,
        selectedCategory: selectedCategory.value,
      },
    });

    // Update only the specific result type
    if (data.results && data.results[resultKey]) {
      results.value[resultKey] = data.results[resultKey];
    } else {
      results.value[resultKey] = [];
    }
  } catch (error) {
    Bugsnag.notify(error);
    results.value[resultKey] = [];
  } finally {
    loadingStates.value[filterType] = false;
  }
};

const globalSearch = _debounce(async () => {
  if (!search.value?.length && !selectedCategory.value) {
    isSearching.value = false;
    // Clear all results
    Object.keys(results.value).forEach((key) => {
      results.value[key] = [];
    });
    return;
  }

  isSearching.value = true;

  // Make parallel API calls for all filter types
  const filterTypes = items.filter((item) => item !== "all");
  await Promise.all(
    filterTypes.map((filterType) => searchByFilter(filterType, search.value))
  );
}, 1000);

const handleFileClick = (file) => {
  if(file.type == 'folder'){
    router.push({
      path: `/resource-manager/${file.id}`,
    });

    closeModal();
    return;
  }

  closeModal();
  fileManagerStore.selectedFile = file;
  fileManagerStore.isViewSelectedFile = true;
  router.push({
    path: '/resource-manager',
    query: { id: file?.id }
  });
};

const handleNewsFeedClick = (newsFeed) => {
  closeModal();
  router.push({
    name: 'Announcement Details',
    params: { id: newsFeed.id }
  });
};

const redirectToSearch = () => {
  globalSearch.cancel();
  router.push({
    name: "search",
    query: {
      search: search.value,
    },
  });

  closeModal();
};
</script>

<template>
  <div>
    <div>
      <SearchIcon
        class="h-6 w-6 text-untitled-gray-400 hover:cursor-pointer"
        @click="openModal"
      />
    </div>
    <TransitionRoot appear :show="isOpen" as="template">
      <Dialog as="div" @close="closeModal" class="relative z-[9999999]">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div
            class="flex min-h-full items-center justify-center p-4 text-center"
          >
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel
                class="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle transition-all max-h-[500px] bg-transparent"
              >
                <div class="mt-3 flex justify-center items-center relative">
                  <input
                    v-model="search"
                    @input="globalSearch"
                    @keypress.enter="redirectToSearch"
                    class="pr-16 block w-full appearance-none rounded-md border border-untitled-gray-300 px-3 py-2 placeholder-untitled-gray-400 shadow-sm focus:border-untitled-gray-500 focus:outline-none focus:ring-untitled-gray-500 text-xl"
                    type="text"
                    placeholder="Start typing to search..."
                  />
                  <span
                    v-if="search?.length"
                    class="absolute right-0 mr-2 text-untitled-gray-500 hover:cursor-pointer"
                    @click="redirectToSearch"
                    >Enter</span
                  >
                </div>
                <div>
                  <div
                    class="mt-3 px-[10px] max-h-[300px] overflow-y-auto bg-white"
                    v-if="isSearching && search?.length"
                  >
                    <div class="mt-3 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Videos</h3>
                      <ul>
                        <li v-if="loadingStates.videos" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="u in results.units?.slice(0, 5)"
                          :key="u.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            :href="`/units/${u.id}`"
                          >
                            <small>{{ u.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.units?.length && !loadingStates.videos">No unit(s) found</li>
                        <li
                          v-if="results.units?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Pryor Courses</h3>
                      <ul>
                        <li v-if="loadingStates['pryor courses']" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="m in results.scorm_courses?.slice(0, 5)"
                          :key="m.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            :href="`/scorm/${m.scorm_provider_id}`"
                          >
                            <small>{{ m.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.scorm_courses?.length && !loadingStates['pryor courses']">No course(s) found</li>
                        <li
                          v-if="results.scorm_courses?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Course</h3>
                      <ul>
                        <li v-if="loadingStates.courses" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="m in results.modules?.slice(0, 5)"
                          :key="m.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            :href="`/units/${m.unit_display.id}`"
                          >
                            <small>{{ m.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.modules?.length && !loadingStates.courses">No course(s) found</li>
                        <li
                          v-if="results.modules?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Modules</h3>
                      <ul>
                        <li v-if="loadingStates.modules" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="m in results.sub_modules?.slice(0, 5)"
                          :key="m.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            :href="`/units/${
                              m.first_unit ? m.first_unit.id : m.unit_display?.id
                            }`"
                          >
                            <small>{{ m.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.sub_modules?.length && !loadingStates.modules">No module(s) found</li>
                        <li
                          v-if="results.sub_modules?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Playlists</h3>
                      <ul>
                        <li v-if="loadingStates.playlists" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="p in results.playlists?.slice(0, 5)"
                          :key="p.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            :href="`/units/${
                              p.first_unit ? p.first_unit.id : p.unit_display?.id
                            }/${p.id}`"
                          >
                            <small>{{ p.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.playlists?.length && !loadingStates.playlists">No playlist(s) found</li>
                        <li
                          v-if="results.playlists?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Users</h3>
                      <ul>
                        <li v-if="loadingStates.users" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="u in results.users?.slice(0, 5)"
                          :key="u.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            :href="`/reports?tab=course-overview&userId=${u?.id}`"
                          >
                            <small>{{ u.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.users?.length && !loadingStates.users">No user(s) found</li>
                        <li
                          v-if="results.users?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                      <h3 class="text-lg text-untitled-gray-500">Resource Manager</h3>
                      <ul>
                        <li v-if="loadingStates['resource manager']" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                        <li
                          class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                          v-for="file in results.files?.slice(0, 5)"
                          :key="file.id"
                        >
                          <a
                            class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                            @click.prevent="handleFileClick(file)"
                          >
                            <small>{{ file.name }}</small>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                            />
                          </a>
                        </li>
                        <li v-if="!results.files?.length && !loadingStates['resource manager']">No Resource Found</li>
                        <li
                          v-if="results.files?.length > 5"
                          class="mt-3 font-bold text-sm"
                        >
                          <small>Press enter for more results</small>
                        </li>
                      </ul>
                    </div>
                      <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                        <h3 class="text-lg text-untitled-gray-500">Categories</h3>
                        <ul>
                          <li v-if="loadingStates.categories" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                          <li
                            class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                            v-for="category in results.categories?.slice(0, 5)"
                            :key="category.id"
                          >
                            <a
                              class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                              :href="`${category?.link}`"
                            >
                              <small>{{ category.name }}</small>
                              <ChevronDoubleRightIcon
                                class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                              />
                            </a>
                          </li>
                          <li v-if="!results.categories?.length && !loadingStates.categories">No unit(s) or course(s) found.</li>
                          <li
                            v-if="results.categories?.length > 5"
                            class="mt-3 font-bold text-sm"
                          >
                            <small>Press enter for more results</small>
                          </li>
                        </ul>
                      </div>
                      <div class="mt-1 border-t-gray-500 pt-3 pb-3">
                        <h3 class="text-lg text-untitled-gray-500">News Feed</h3>
                        <ul>
                          <li v-if="loadingStates['news feed']" class="mt-2 text-sm text-untitled-gray-400">Loading...</li>
                          <li
                            class="mt-2 text-sm border-b-2 border-b-untitled-gray-200 hover:cursor-pointer hover:bg-slate-200 p-1"
                            v-for="newsFeed in results.news_feeds?.slice(0, 5)"
                            :key="newsFeed.id"
                          >
                            <a
                              class="flex flex-row items-center text-untitled-gray-500 justify-between w-100"
                              @click.prevent="handleNewsFeedClick(newsFeed)"
                            >
                              <small>{{ newsFeed.title }}</small>
                              <ChevronDoubleRightIcon
                                class="h-4 w-4 text-untitled-gray-400 hover:cursor-pointer"
                              />
                            </a>
                          </li>
                          <li v-if="!results.news_feeds?.length && !loadingStates['news feed']">No news feed(s) found</li>
                          <li
                            v-if="results.news_feeds?.length > 5"
                            class="mt-3 font-bold text-sm"
                          >
                            <small>Press enter for more results</small>
                          </li>
                        </ul>
                      </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
